import { PipelineHasWaitingRunsErrorDTO, PipelineModifiedErrorDTO, PipelineNodesInUseErrorDTO, PipelineStepStatsDTO, } from '@wit-support/admin-contracts';
import { createModuleApi } from '@wit-support/admin-client-core';
import { FORBIDDEN_RU, issuesFromValidatorBody, issuesRu, NETWORK_ERROR_RU, SESSION_EXPIRED_RU, } from './errors.js';
const api = createModuleApi({ errorFormat: 'server-message' });
export function setPipelinesApiBaseUrl(url) {
    api.setBaseUrl(url);
}
export async function listPipelines() {
    return api.get('/api/pipelines');
}
/** One pipeline with its definition — what the editor opens (and clones from). */
export async function getPipeline(id) {
    return api.get(`/api/pipelines/${encodeURIComponent(id)}`);
}
/**
 * A refused create/update with what the editor needs to react: the HTTP
 * status, the server's machine code, and — for `409 pipeline_nodes_in_use` —
 * the steps to mark on the canvas. `message` is already a sentence for a
 * person: the module's `server-message` unwrap would surface the bare code
 * («pipeline_nodes_in_use») and drop `nodes`, so these two calls read the body
 * themselves.
 */
export class PipelineSaveError extends Error {
    status;
    code;
    nodesInUse;
    currentVersion;
    constructor(message, 
    /** HTTP status; `0` — the request never reached the server (network). */
    status, code, nodesInUse = [], 
    /** `409 pipeline_modified`: the version stored now. */
    currentVersion = null) {
        super(message);
        this.status = status;
        this.code = code;
        this.nodesInUse = nodesInUse;
        this.currentVersion = currentVersion;
        this.name = 'PipelineSaveError';
    }
}
const SAVE_ERROR_RU = {
    pipeline_key_exists: 'Пайплайн с таким ключом уже есть — выберите другой ключ.',
    not_found: 'Пайплайн не найден — возможно, его удалили.',
    pipeline_modified: 'Пайплайн изменили, пока вы работали.',
};
async function saveErrorFrom(r) {
    let body = null;
    try {
        body = await r.json();
    }
    catch {
        // not JSON — fall through to the status line
    }
    const inUse = PipelineNodesInUseErrorDTO.safeParse(body);
    if (inUse.success) {
        return new PipelineSaveError(inUse.data.message, r.status, inUse.data.error, inUse.data.nodes);
    }
    if (r.status === 401)
        return new PipelineSaveError(SESSION_EXPIRED_RU, 401, 'unauthorized');
    if (r.status === 403)
        return new PipelineSaveError(FORBIDDEN_RU, 403, 'forbidden');
    const issues = issuesFromValidatorBody(body);
    if (issues !== null) {
        return new PipelineSaveError(`Сервер не принял данные: ${issuesRu(issues)}`, r.status, 'validation');
    }
    const modified = PipelineModifiedErrorDTO.safeParse(body);
    if (modified.success) {
        return new PipelineSaveError(SAVE_ERROR_RU.pipeline_modified, r.status, modified.data.error, [], modified.data.currentVersion);
    }
    const data = (body ?? {});
    const code = typeof data.error === 'string' ? data.error : null;
    if (code === 'unknown_trigger_topic' && Array.isArray(data.keys)) {
        return new PipelineSaveError(`В условии запуска есть темы, которых больше нет: ${data.keys.join(', ')}`, r.status, code);
    }
    const known = code !== null ? SAVE_ERROR_RU[code] : undefined;
    const message = known ??
        (typeof data.message === 'string' ? data.message : null) ??
        (code !== null ? `Не удалось сохранить: ${code}` : null) ??
        `Не удалось сохранить: сервер ответил ошибкой ${r.status}`;
    return new PipelineSaveError(message, r.status, code);
}
async function sendPipeline(method, path, body) {
    let r;
    try {
        r = await api.fetchRaw(path, {
            method,
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify(body),
        });
    }
    catch {
        // fetch rejects only when no response came back at all (offline, DNS, CORS).
        throw new PipelineSaveError(NETWORK_ERROR_RU, 0, 'network');
    }
    if (!r.ok)
        throw await saveErrorFrom(r);
    return (await r.json());
}
/** Create. A refusal throws {@link PipelineSaveError}. */
export async function createPipeline(body) {
    return sendPipeline('POST', '/api/pipelines', body);
}
/**
 * Partial update. A refusal throws {@link PipelineSaveError} — notably `409
 * pipeline_nodes_in_use` with the steps parked runs still need, and `409
 * pipeline_modified` when `expectedVersion` (the version of the copy the
 * author opened or last saved) is behind — never retried with the stored
 * version: that would overwrite the colleague's edit after all.
 */
export async function updatePipeline(id, body) {
    return sendPipeline('PATCH', `/api/pipelines/${encodeURIComponent(id)}`, body);
}
/**
 * `DELETE` refused because runs of the pipeline are parked (`409
 * pipeline_has_waiting_runs`): they would fail on resume. The author confirms
 * that, then {@link deletePipeline} is called again with `expectedWaiting` =
 * this `count`; more parked by then → refused again with the new count.
 */
export class PipelineHasWaitingRunsError extends Error {
    count;
    constructor(count) {
        super('В пайплайне ждут ответа клиенты');
        this.count = count;
        this.name = 'PipelineHasWaitingRunsError';
    }
}
/**
 * Soft-delete. Without `expectedWaiting` the server refuses while runs are
 * parked — {@link PipelineHasWaitingRunsError}. With it (`?force=true&
 * expectedWaiting=N`) — only after the author confirmed that N dialogs will
 * fail and go back to the operators; the server recounts and refuses again if
 * more are parked now.
 */
export async function deletePipeline(id, opts = {}) {
    const r = await api.del(`/api/pipelines/${encodeURIComponent(id)}${opts.expectedWaiting !== undefined
        ? `?force=true&expectedWaiting=${encodeURIComponent(String(opts.expectedWaiting))}`
        : ''}`);
    if (r.status === 409) {
        const body = PipelineHasWaitingRunsErrorDTO.safeParse(await r.json().catch(() => null));
        if (body.success)
            throw new PipelineHasWaitingRunsError(body.data.count);
    }
    if (!r.ok)
        throw new Error(`delete failed ${r.status}`);
}
export async function listPipelineRuns(pipelineId) {
    const body = await api.get(`/api/pipelines/${pipelineId}/runs`);
    return body.items;
}
export async function getPipelineRun(runId) {
    return api.get(`/api/pipeline-runs/${runId}`);
}
/**
 * Per-node traffic of one pipeline over the last `days` days (1..365, server
 * default 30) plus the runs parked on each node right now — the numbers the
 * builder draws on its arrows and «ждут ответа» badges.
 *
 * `null` on 404 — the route is not served (an admin-api older than the
 * builder, rollout window) or the pipeline is gone. That is «НЕ ИЗВЕСТНО»,
 * never «никто не ждёт»: such an admin-api has no 409 `pipeline_nodes_in_use`
 * either, so until a good answer arrives in this entry the editor locks saved
 * steps against removal and retyping (see `graph/waiting.ts`
 * `unverifiedLockOf`), it does not merely hide the numbers; after one, the
 * last good answer stays in force (`routes/editor.tsx`). A body that does
 * not parse as `PipelineStepStatsDTO` throws (an empty `waiting` read off a
 * wrong body would look like «никто не ждёт»).
 */
export async function getPipelineStepStats(id, days) {
    const r = await api.fetchRaw(`/api/pipelines/${encodeURIComponent(id)}/step-stats?days=${encodeURIComponent(String(days))}`);
    if (r.status === 404)
        return null;
    const parsed = PipelineStepStatsDTO.safeParse(await api.unwrap(r));
    if (!parsed.success)
        throw new Error('Сервер вернул статистику в непонятном виде');
    return parsed.data;
}
/**
 * The `send_template_email` action picks a template from the existing reply
 * templates list — fetched here so the editor's Select can offer them by name.
 */
export async function listTemplatesForPicker() {
    const body = await api.get('/api/templates');
    return body.items;
}
/**
 * The trigger editor offers active classification topics as the leaf options of
 * the run condition — fetched here so the `topic` Select can render them by name.
 */
export async function listTopicsForTrigger() {
    const topics = await api.get('/api/topics');
    return topics.map((t) => ({ key: t.key, name: t.name }));
}
//# sourceMappingURL=api.js.map